<template>
  <!--
    Open on load by design. MAI-4601 feedback first read "collapsed on load",
    but the designer reconsidered on that item ("easy to miss closed… should we
    leave it open?") and we settled on leaving it expanded.
  -->
  <Accordion :value="['0']" multiple>
    <AccordionPanel value="0" :pt="{ root: { class: '!border-0' } }">
      <AccordionHeader
        :pt="{
          root: {
            class:
              'bg-gray-100 py-4 px-6 flex justify-between items-center w-full',
          },
        }"
      >
        Changes &amp; transactions
      </AccordionHeader>
      <AccordionContent :pt="{ content: { class: 'pt-4 pb-6 px-6' } }">
        <div v-if="isLoading" class="flex justify-center p-4">
          <ProgressSpinner style="width: 1.5rem; height: 1.5rem" />
        </div>
        <div v-else-if="error">
          <Message severity="error">Failed to load activity</Message>
        </div>
        <div
          v-else-if="props.entries.length === 0"
          class="text-sm text-gray-500 py-2"
        >
          No history yet.
        </div>
        <div v-else class="divide-y divide-gray-200">
          <div
            v-for="entry in props.entries"
            :key="entry.id"
            class="space-y-1 py-4 first:pt-0 last:pb-0"
          >
            <div class="text-xs text-gray-500">
              {{ formatTimestamp(entry.created_at) }}
            </div>

            <div class="flex items-start gap-3 rounded-md bg-gray-200 p-3">
              <div
                class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                :class="iconBgClass(entry.entry_type)"
              >
                <i :class="iconClass(entry.entry_type)" aria-hidden="true" />
              </div>
              <div class="flex-1 min-w-0 space-y-2">
                <div>
                  <div class="font-medium">{{ headlineFor(entry) }}</div>

                  <div
                    v-if="oldNewFor(entry)"
                    class="text-sm text-gray-700 mt-1"
                  >
                    <span class="text-gray-400 line-through">
                      {{ oldNewFor(entry)?.oldValue }}
                    </span>
                    <span class="ml-1">{{ oldNewFor(entry)?.newValue }}</span>
                  </div>

                  <div
                    v-else-if="amountFor(entry)"
                    class="text-sm text-gray-700 mt-1"
                  >
                    {{ amountFor(entry) }}
                  </div>

                  <div
                    v-if="entry.payment_method_label"
                    class="text-sm text-gray-700 mt-1"
                  >
                    {{ entry.payment_method_label }}
                  </div>
                </div>

                <Tag
                  v-if="entry.amendment_tag"
                  :value="entry.amendment_tag.name"
                  severity="info"
                />

                <div
                  v-if="entry.notes"
                  class="text-sm italic text-gray-700 border-l-2 border-gray-300 pl-3"
                >
                  "{{ entry.notes }}"
                </div>
              </div>
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionPanel>
  </Accordion>
</template>

<script setup lang="ts">
import Accordion from "primevue/accordion";
import AccordionPanel from "primevue/accordionpanel";
import AccordionHeader from "primevue/accordionheader";
import AccordionContent from "primevue/accordioncontent";
import ProgressSpinner from "primevue/progressspinner";
import Message from "primevue/message";
import Tag from "primevue/tag";
import type { ActivityFeedEntry } from "@/api/model";
import { EntryTypeEnum } from "@/api/model";
import {
  DateFormat,
  getFormattedCurrency,
  getFormattedDate,
} from "@/shared/utils/helpers";

interface Props {
  entries: ActivityFeedEntry[];
  isLoading?: boolean;
  error?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  error: null,
});

const ENTRY_ICON: Record<EntryTypeEnum, string> = {
  [EntryTypeEnum.amount_changed]: "pi pi-pencil",
  [EntryTypeEnum.date_changed]: "pi pi-calendar",
  [EntryTypeEnum.method_changed]: "pi pi-credit-card",
  [EntryTypeEnum.waived]: "pi pi-trash",
  [EntryTypeEnum.refunded]: "pi pi-replay",
  [EntryTypeEnum.canceled]: "pi pi-times",
  [EntryTypeEnum.transaction_succeeded]: "pi pi-check",
  [EntryTypeEnum.transaction_failed]: "pi pi-times",
  [EntryTypeEnum.transaction_refunded]: "pi pi-replay",
};

const ENTRY_ICON_BG: Record<EntryTypeEnum, string> = {
  [EntryTypeEnum.amount_changed]: "bg-blue-100 text-blue-600",
  [EntryTypeEnum.date_changed]: "bg-blue-100 text-blue-600",
  [EntryTypeEnum.method_changed]: "bg-blue-100 text-blue-600",
  [EntryTypeEnum.waived]: "bg-gray-200 text-gray-700",
  [EntryTypeEnum.refunded]: "bg-orange-100 text-orange-600",
  [EntryTypeEnum.canceled]: "bg-gray-200 text-gray-700",
  [EntryTypeEnum.transaction_succeeded]: "bg-green-100 text-green-700",
  [EntryTypeEnum.transaction_failed]: "bg-red-100 text-red-700",
  [EntryTypeEnum.transaction_refunded]: "bg-orange-100 text-orange-600",
};

const iconClass = (entryType: EntryTypeEnum): string =>
  ENTRY_ICON[entryType] ?? "pi pi-info-circle";

const iconBgClass = (entryType: EntryTypeEnum): string =>
  ENTRY_ICON_BG[entryType] ?? "bg-gray-200 text-gray-700";

const formatTimestamp = (iso: string): string =>
  getFormattedDate(iso, DateFormat.DATETIME) || iso;

const actorSuffix = (entry: ActivityFeedEntry): string =>
  entry.actor?.name ? ` by ${entry.actor.name}` : "";

const headlineFor = (entry: ActivityFeedEntry): string => {
  switch (entry.entry_type) {
    case EntryTypeEnum.amount_changed:
      return `Amount changed${actorSuffix(entry)}`;
    case EntryTypeEnum.date_changed:
      return `Date changed${actorSuffix(entry)}`;
    case EntryTypeEnum.method_changed:
      return `Method changed${actorSuffix(entry)}`;
    case EntryTypeEnum.waived:
      return `Waived${actorSuffix(entry)}`;
    case EntryTypeEnum.refunded:
      return `Refunded${actorSuffix(entry)}`;
    case EntryTypeEnum.canceled:
      return `Cancelled${actorSuffix(entry)}`;
    case EntryTypeEnum.transaction_succeeded:
      return "Successful transaction";
    case EntryTypeEnum.transaction_failed:
      return "Failed transaction";
    case EntryTypeEnum.transaction_refunded:
      return "Refunded transaction";
    default:
      return entry.entry_type;
  }
};

interface OldNew {
  oldValue: string;
  newValue: string;
}

const oldNewFor = (entry: ActivityFeedEntry): OldNew | null => {
  const meta = entry.metadata ?? {};
  const m = meta as Record<string, unknown>;
  if (
    entry.entry_type === EntryTypeEnum.amount_changed ||
    entry.entry_type === EntryTypeEnum.waived
  ) {
    if (m.old_amount != null && m.new_amount != null) {
      const oldNum = parseFloat(String(m.old_amount));
      const newNum = parseFloat(String(m.new_amount));
      if (Number.isFinite(oldNum) && Number.isFinite(newNum)) {
        return {
          oldValue: getFormattedCurrency(oldNum),
          newValue: getFormattedCurrency(newNum),
        };
      }
    }
  }
  if (entry.entry_type === EntryTypeEnum.date_changed) {
    if (m.old_date && m.new_date) {
      return {
        oldValue: formatDateOnly(String(m.old_date)),
        newValue: formatDateOnly(String(m.new_date)),
      };
    }
  }
  if (entry.entry_type === EntryTypeEnum.method_changed) {
    if (m.old_method_label && m.new_method_label) {
      return {
        oldValue: String(m.old_method_label),
        newValue: String(m.new_method_label),
      };
    }
  }
  return null;
};

const amountFor = (entry: ActivityFeedEntry): string | null => {
  const meta = (entry.metadata ?? {}) as Record<string, unknown>;
  if (
    entry.entry_type === EntryTypeEnum.refunded &&
    meta.refund_amount != null
  ) {
    const num = parseFloat(String(meta.refund_amount));
    if (Number.isFinite(num)) {
      return getFormattedCurrency(num);
    }
  }
  return null;
};

// Use date-fns parseISO via getFormattedDate so "YYYY-MM-DD" parses as local
// midnight; the previous `new Date(iso)` call parsed it as UTC midnight, which
// rendered as the day before in negative-offset timezones (e.g. MDT).
const formatDateOnly = (iso: string): string =>
  getFormattedDate(iso, "MMM d, yyyy") || iso;
</script>
